import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, MAT_TYPE } from "@/lib/ui";
import { toast } from "sonner";
import { Pencil, RotateCcw, Check, X, Loader2 } from "lucide-react";

const TYPES = [
  { key: "", label: "Tümü" },
  { key: "tel", label: "Tel" },
  { key: "tela", label: "Tela" },
  { key: "kraft", label: "Kraft" },
  { key: "lamet", label: "Lamet" },
  { key: "cring", label: "C-Ring" },
  { key: "holtmelt", label: "Holtmelt" },
  { key: "celik_cember", label: "Çelik Çember" },
  { key: "tahta_palet", label: "Tahta Palet" },
  { key: "round_frame", label: "Round Frame" },
];

export default function Consumption() {
  const { isEditor } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [type, setType] = useState("");
  const [data, setData] = useState({ rows: [], month_names: [] });
  const [loading, setLoading] = useState(true);
  const [editCell, setEditCell] = useState(null); // {key, month}
  const [editVal, setEditVal] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/consumption-summary?year=${year}${type ? `&type=${type}` : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [year, type]);
  useEffect(load, [load]);

  const startEdit = (key, month, value) => {
    if (!isEditor) return;
    setEditCell({ key, month });
    setEditVal(String(value ?? ""));
  };

  const saveEdit = async () => {
    if (!editCell) return;
    setSaving(true);
    try {
      await api.put("/consumption-summary", {
        material_key: editCell.key, month: editCell.month,
        qty: parseFloat(editVal) || 0, year,
      });
      toast.success("Tüketim güncellendi");
      setEditCell(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const resetCell = async (key, month) => {
    try {
      await api.put("/consumption-summary", { material_key: key, month, year, reset: true });
      toast.success("Düzeltme kaldırıldı");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  return (
    <div className="space-y-6 animate-in-up" data-testid="consumption-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight">Tüm Tüketimler</h1>
          <p className="text-[#94A3B8] text-sm mt-1">
            Her malzemenin aylık gerçekleşen tüketimi ve açık (üretilmemiş) siparişlerin öngörülen tüketimi.
            {isEditor && " Hücreye tıklayarak elle düzeltebilirsiniz."}
          </p>
        </div>
        <select data-testid="consumption-year-select" value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          className="bg-[#131A26] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {TYPES.map((t) => (
          <button key={t.key} data-testid={`consumption-type-${t.key || "all"}`} onClick={() => setType(t.key)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              type === t.key ? "bg-[#2563EB] text-white" : "bg-[#131A26] border border-[#212D40] text-[#94A3B8] hover:text-white"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-[#131A26] border border-[#212D40] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto">
          <table className="w-full text-sm whitespace-nowrap" data-testid="consumption-table">
            <thead className="sticky top-0 bg-[#0F1520] z-10">
              <tr className="text-left text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#212D40]">
                <th className="py-3 px-4 sticky left-0 bg-[#0F1520] z-20">Malzeme</th>
                {data.month_names.map((mn) => (
                  <th key={mn} className="py-3 px-3 text-right">{mn}</th>
                ))}
                <th className="py-3 px-4 text-right text-[#60A5FA]">Açık Siparişler</th>
                <th className="py-3 px-4 text-right">Yıl Toplam</th>
              </tr>
            </thead>
            <tbody>
              {!loading && data.rows.map((r) => {
                const t = MAT_TYPE[r.type] || {};
                return (
                  <tr key={r.material_key} className="border-b border-[#1a2334] hover:bg-[#0F1520]"
                    data-testid={`consumption-row-${r.material_key}`}>
                    <td className="py-2.5 px-4 sticky left-0 bg-[#131A26] z-10">
                      <span className="flex items-center gap-2">
                        <span style={{ color: t.color }}>●</span>
                        <span className="font-medium">{r.name}</span>
                        <span className="text-xs text-[#64748B]">{r.unit}</span>
                      </span>
                    </td>
                    {r.months.map((m, i) => {
                      const isEd = editCell && editCell.key === r.material_key && editCell.month === i + 1;
                      return (
                        <td key={i} className="py-2 px-3 text-right group"
                          data-testid={`consumption-cell-${r.material_key}-${i + 1}`}>
                          {isEd ? (
                            <span className="flex items-center gap-1 justify-end">
                              <input autoFocus type="number" value={editVal}
                                onChange={(e) => setEditVal(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditCell(null); }}
                                className="w-20 bg-[#0F1520] border border-[#2563EB] rounded px-1.5 py-1 text-right font-num outline-none" />
                              <button onClick={saveEdit} disabled={saving} data-testid="consumption-save-cell" className="text-[#10B981]">
                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              </button>
                              <button onClick={() => setEditCell(null)} className="text-[#64748B]"><X className="w-3.5 h-3.5" /></button>
                            </span>
                          ) : (
                            <span className={`font-num inline-flex items-center gap-1 justify-end ${m.manual ? "text-[#F59E0B]" : m.value ? "" : "text-[#3a4658]"}`}
                              onClick={() => startEdit(r.material_key, i + 1, m.value)}
                              style={{ cursor: isEditor ? "pointer" : "default" }}>
                              {fmt(m.value)}
                              {m.manual && isEditor && (
                                <button onClick={(e) => { e.stopPropagation(); resetCell(r.material_key, i + 1); }}
                                  title="Düzeltmeyi kaldır" className="text-[#64748B] hover:text-[#EF4444]">
                                  <RotateCcw className="w-3 h-3" />
                                </button>
                              )}
                              {isEditor && !m.manual && <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-40" />}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-2.5 px-4 text-right font-num text-[#60A5FA] font-semibold">{fmt(r.open)}</td>
                    <td className="py-2.5 px-4 text-right font-num font-semibold">{fmt(r.year_total)}</td>
                  </tr>
                );
              })}
              {loading && (
                <tr><td colSpan={15} className="py-10 text-center text-[#64748B]">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Yükleniyor...
                </td></tr>
              )}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={15} className="py-10 text-center text-[#64748B]">
                  Bu yıl için tüketim kaydı veya açık sipariş bulunamadı.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-[#64748B]">
        <span className="text-[#F59E0B]">Turuncu</span> değerler elle düzeltilmiş kayıtları gösterir.
        Geçmiş tüketimler, siparişler "Üretildi" olarak işaretlendiğinde otomatik oluşur.
      </p>
    </div>
  );
}
